import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { usePageMeta } from "@/hooks/usePageMeta";
import { topics } from "@/mocks/topics";
import CommentSection from "./components/CommentSection";
import RelatedTopics from "./components/RelatedTopics";

export default function TopicDetail() {
  const { id } = useParams();
  const topic = topics.find((t) => t.id === id);

  const related = useMemo(
    () => topics.filter((t) => t.id !== id && t.categoryId === topic?.categoryId).slice(0, 4),
    [id, topic]
  );

  usePageMeta({
    title: topic ? `${topic.title} - 17吃瓜社区` : "话题不存在 - 17吃瓜社区",
    description: topic
      ? `${topic.summary} 在17吃瓜社区参与讨论,看看其他网友的不同视角。`
      : "你访问的话题可能已经下线,回到话题广场看看其他正在讨论的内容。",
    keywords: topic ? `${topic.tags.join(",")},17.cn,17.cn吃瓜社区,17.cn网友讨论` : "17.cn,17.cn吃瓜社区",
    path: `/topic/${id}`,
    jsonLd: topic
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: topic.title,
          description: topic.summary,
          articleSection: topic.categoryName,
          keywords: topic.tags.join(","),
          author: { "@type": "Person", name: topic.author },
          publisher: { "@type": "Organization", name: "17吃瓜社区" },
          mainEntityOfPage: `https://17.cn/topic/${topic.id}`,
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://17.cn/" },
              { "@type": "ListItem", position: 2, name: "话题广场", item: "https://17.cn/topics" },
              { "@type": "ListItem", position: 3, name: topic.title, item: `https://17.cn/topic/${topic.id}` },
            ],
          },
        }
      : undefined,
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4">
          <div className="text-center py-24">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500 mb-5">
              <i className="ri-window-line text-3xl"></i>
            </span>
            <h1 className="font-heading font-bold text-2xl text-foreground-950">这个话题暂时找不到了</h1>
            <p className="text-sm text-foreground-600 mt-3">它可能已经下线,或者链接不太对。回到广场看看别的吧。</p>
            <Link
              to="/topics"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回话题广场
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="px-4 md:px-10 pt-8 md:pt-10 pb-4">
          <nav aria-label="面包屑" className="text-xs md:text-sm text-foreground-500">
            <ol className="flex items-center gap-2 flex-wrap">
              <li><Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">首页</Link></li>
              <li aria-hidden="true" className="text-foreground-300">/</li>
              <li><Link to="/topics" className="hover:text-primary-700 transition-colors cursor-pointer">话题广场</Link></li>
              <li aria-hidden="true" className="text-foreground-300">/</li>
              <li><Link to={`/topics?category=${topic.categoryId}`} className="hover:text-primary-700 transition-colors cursor-pointer">{topic.categoryName}</Link></li>
            </ol>
          </nav>
        </div>

        <div className="px-4 md:px-10 pb-20 md:pb-24 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            <header className="mb-8">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <Link to={`/topics?category=${topic.categoryId}`} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer">
                  {topic.categoryName}
                </Link>
                {topic.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600">#{tag}</span>
                ))}
              </div>
              <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">{topic.title}</h1>
              <div className="flex items-center gap-4 mt-5 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-900 text-xs font-medium">
                    {topic.author.slice(0, 1)}
                  </span>
                  {topic.author}
                </span>
                <span className="text-foreground-300">·</span>
                <span>{topic.updatedAt}</span>
                <span className="text-foreground-300">·</span>
                <span>约 {topic.readingMinutes} 分钟读完</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-200 mb-8">
              <img
                src={topic.cover}
                alt={`${topic.title} - ${topic.categoryName}话题配图`}
                title={`${topic.title} ${topic.categoryName}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex flex-col gap-5">
              {topic.content.map((p, i) => (
                <p key={i} className="text-base md:text-[17px] text-foreground-700 leading-[1.9]">{p}</p>
              ))}
            </div>

            <div className="mt-10">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-discuss-line text-primary-600"></i>
                网友观点
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topic.opinions.map((o) => (
                  <div key={o.user} className="p-5 rounded-lg bg-background-100 border border-background-200/80">
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 mb-3">{o.stance}</span>
                    <p className="text-sm text-foreground-700 leading-relaxed">{o.text}</p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                      <span className="text-xs text-foreground-600">{o.user}</span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-thumb-up-line"></i>{o.like}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <CommentSection initialComments={topic.comments} />
            </div>
          </article>

          <aside className="lg:col-span-1 flex flex-col gap-6">
            <div className="rounded-lg bg-primary-950 text-background-100 p-6">
              <h2 className="font-heading font-bold text-lg text-background-50 mb-4">话题热度</h2>
              <div className="flex flex-col gap-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-background-100/80 flex items-center gap-2"><i className="ri-fire-line text-accent-300"></i>热度值</span>
                  <span className="font-heading font-bold text-background-50">{topic.heat.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-background-100/80 flex items-center gap-2"><i className="ri-chat-3-line text-accent-300"></i>讨论数</span>
                  <span className="font-heading font-bold text-background-50">{topic.commentCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-background-100/80 flex items-center gap-2"><i className="ri-thumb-up-line text-accent-300"></i>点赞数</span>
                  <span className="font-heading font-bold text-background-50">{topic.likeCount.toLocaleString()}</span>
                </div>
              </div>
              <a
                href="#comments"
                className="mt-6 w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-chat-smile-2-line"></i>
                去评论区聊两句
              </a>
            </div>

            <RelatedTopics topics={related} />
          </aside>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}